import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import serviceApi from '../../services/serviceApi';
import ClientLayout from '../../components/ClientLayout';
import '../../styles/ClientLayout.css';
import { WHATSAPP_NUMBER } from '../../config/clientPortalConstants';
import '../../styles/ClientLocations.css';

/**
 * ClientLocations — read-only page showing client's subscribed locations.
 * Accessed via /client/t/:token/locations (public route, token-based auth).
 * Uses ClientLayout wrapper. NO AuthContext, NO DesktopLayout.
 *
 * Fetches from the existing dashboard endpoint (locations are already returned).
 * Shows a "قريباً" banner — location management features are planned for later.
 */
const ClientLocations = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await serviceApi.clientPortal.getDashboard(token);
      const d = res.data || {};
      setLocations(d.locations || []);
      setClientName(d.clientName || '');
      setClientEmail(d.clientEmail || '');
    } catch (err) {
      console.error('Client locations error:', err);
      const status = err.response?.status;
      if (status === 401) {
        setError('invalid_token');
      } else {
        setError('network');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="cd-layout-loading">
        <div className="cd-layout-loading__spinner" />
        <span className="cd-layout-loading__text">جاري التحميل...</span>
      </div>
    );
  }

  // ── Error: invalid token ──
  if (error === 'invalid_token') {
    return (
      <div className="cd-layout-error">
        <div className="cd-layout-error__icon"><i className="bi bi-shield-x" /></div>
        <div className="cd-layout-error__title">رابط غير صالح</div>
        <div className="cd-layout-error__sub">
          هذا الرابط لم يعد صالحاً أو غير صحيح.
          <br />
          تواصل مع فريق الدعم للحصول على رابط جديد.
        </div>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('مرحباً، أحتاج رابط جديد للوحة خدمات النظافة')}`}
          className="cd-layout-error__wa"
        >
          <i className="bi bi-whatsapp" />
          تواصل عبر واتساب
        </a>
      </div>
    );
  }

  // ── Error: network ──
  if (error === 'network') {
    return (
      <div className="cd-layout-error">
        <div className="cd-layout-error__icon"><i className="bi bi-wifi-off" /></div>
        <div className="cd-layout-error__title">تعذر الاتصال</div>
        <div className="cd-layout-error__sub">تحقق من اتصال الإنترنت وحاول مرة أخرى</div>
        <button onClick={fetchData} className="cd-layout-error__retry">
          <i className="bi bi-arrow-clockwise" style={{ marginInlineStart: 6 }} />
          إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <ClientLayout activeTab="locations" clientName={clientName} clientEmail={clientEmail}>
      <div className="cd-locs">
        {/* Page Header */}
        <div className="cd-locs__header">
          <span className="cd-locs__title">مواقعي</span>
          {locations.length > 0 && (
            <span className="cd-locs__count">{locations.length}</span>
          )}
        </div>

        {/* Coming Soon Banner */}
        <div className="cd-locs__banner">
          <div className="cd-locs__banner-icon">
            <i className="bi bi-stars" />
          </div>
          <div className="cd-locs__banner-text">
            <strong>قريباً</strong> — ستتمكن من إدارة مواقعك وتفاصيلها من هنا
          </div>
        </div>

        {/* Locations List */}
        {locations.length === 0 ? (
          <div className="cd-locs__empty">
            <div className="cd-locs__empty-icon">
              <i className="bi bi-geo-alt" />
            </div>
            <div className="cd-locs__empty-title">لا توجد مواقع مسجلة</div>
            <div className="cd-locs__empty-sub">
              تواصل مع فريق الدعم لإضافة مواقعك
            </div>
          </div>
        ) : (
          locations.map(loc => (
            <div key={loc._id} className="cd-locs__card">
              <div className="cd-locs__card-icon">
                <i className="bi bi-building" />
              </div>
              <div className="cd-locs__card-info">
                <span className="cd-locs__card-name">{loc.name}</span>
                {loc.locationCode && (
                  <span className="cd-locs__card-code">{loc.locationCode}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientLocations;
